import { Withdrawal } from "@apis/index";
import { useGeneralSettings } from "@hooks/useGeneralSettings";
import Helper from "@utils/helper";
import Grid from "@components/grid";
import styled from "@emotion/styled";
import {
  Banknote,
  CheckCircle2,
  Clock,
  CreditCard,
  XCircle,
  TrendingUp,
  CheckCheck,
  AlertCircle,
} from "lucide-react";
import React, { useEffect, useState } from "react";

const StatsContainer = styled.div`
  margin-bottom: 24px;
`;

const Card = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  border: 1px solid rgba(229, 231, 235, 0.5);
  display: flex;
  flex-direction: column;
  gap: 16px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow:
    0 4px 6px -1px rgba(0, 0, 0, 0.05),
    0 2px 4px -1px rgba(0, 0, 0, 0.03);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 4px;
    background: ${(props: any) => props.color};
    opacity: 0.8;
  }

  &:hover {
    box-shadow:
      0 20px 25px -5px rgba(0, 0, 0, 0.1),
      0 10px 10px -5px rgba(0, 0, 0, 0.04);
    transform: translateY(-4px);
  }

  &:hover .icon-wrapper {
    background: ${(props: any) => props.color};
    color: white;
    transform: scale(1.1) rotate(5deg);
  }
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const IconWrapper = styled.div<{ color: string }>`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(props) => `${props.color}10`};
  color: ${(props) => props.color};
  transition: all 0.3s ease;
`;

const ContentColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Value = styled.div`
  font-size: 28px;
  font-weight: 800;
  color: #111827;
  letter-spacing: -0.02em;
  font-feature-settings: "tnum";
`;

const Title = styled.div`
  font-size: 13px;
  color: #6b7280;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const WithdrawalStats = ({ filters }: { filters?: any }) => {
  const [stats, setStats] = useState<any>(null);
  const { settings } = useGeneralSettings();

  const fetchStats = async () => {
    try {
      const res = await Withdrawal.getStats({ params: filters });
      setStats(res);
    } catch (e) {
      console.error("Failed to fetch stats", e);
    }
  };

  useEffect(() => {
    fetchStats();
  }, [filters]);

  if (!stats) return null;

  const cards = [
    {
      title: "Total Requests",
      value: stats.totalRequests,
      icon: <CreditCard size={24} />,
      color: "#3b82f6", // Blue
    },
    {
      title: "Total Due",
      value: Helper.formatCurrency(stats.totalDue, settings),
      icon: <Banknote size={24} />,
      color: "#8b5cf6", // Violet
    },
    {
      title: "Total Paid",
      value: Helper.formatCurrency(stats.totalApprovedAmount || 0, settings),
      icon: <CheckCheck size={24} />,
      color: "#059669", // Emerald Dark
    },
    {
      title: "Pending Amount",
      value: Helper.formatCurrency(stats.pendingAmount || 0, settings),
      icon: <Clock size={24} />,
      color: "#d97706", // Amber Dark
    },
    {
      title: "Pending Count",
      value: stats.pendingRequests,
      icon: <AlertCircle size={24} />,
      color: "#f59e0b", // Amber
    },
    {
      title: "Approved Count",
      value: stats.approvedRequests,
      icon: <CheckCircle2 size={24} />,
      color: "#10b981", // Emerald
    },
    {
      title: "Rejected Count",
      value: stats.rejectedRequests,
      icon: <XCircle size={24} />,
      color: "#ef4444", // Red
    },
    {
      title: "Rejected Amount",
      value: Helper.formatCurrency(stats.rejectedAmount || 0, settings),
      icon: <XCircle size={24} />,
      color: "#b91c1c", // Red Dark
    },
  ];

  return (
    <StatsContainer>
      <Grid repeat={4} styleGrid="gap: 20px; place-items: stretch; padding:0">
        {cards.map((card, i) => (
          <Card key={i} color={card.color}>
            <HeaderRow>
              <ContentColumn>
                <Title>{card.title}</Title>
                <Value>{card.value}</Value>
              </ContentColumn>
              <IconWrapper color={card.color} className="icon-wrapper">
                {card.icon}
              </IconWrapper>
            </HeaderRow>
          </Card>
        ))}
      </Grid>
    </StatsContainer>
  );
};

export default WithdrawalStats;
